import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { Button, EnumHelperText, HelperText, Typography } from 'shared/ui'
import { AIAgent } from 'icons/custom'
import { type MergeField } from 'entities/MergeField/model/MergeField'
import { type IUser } from 'entities/Users'
import type { IChatMessageUI } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/store/chatStore'
import { renderMessageContent } from 'pages/chatbot/ui/UnifiedAgent/lib/formatWidgetMessage'
import styles from './style.module.scss'

type IProps = {
  messages: IChatMessageUI[]
  retry: () => void
  sendMessage: (message: string) => void
  allMergeFields: MergeField[]
  usersMap: Map<number, IUser>
  isLoading: boolean
}

export const Messages = observer(
  ({ messages, retry, sendMessage, allMergeFields, usersMap, isLoading }: IProps) => {
    const getLastUserMessageIndex = (role: string) =>
      messages.findLastIndex((message) => message.role === role)

    return (
      <>
        {messages.map((msg, index) => (
          <div key={index} className={styles.messageGroup}>
            <div
              className={
                msg.role === 'user'
                  ? classnames(styles.messageUser, {
                      [styles.messageUserError]: msg.hasError,
                    })
                  : styles.messageAssistant
              }
            >
              {msg.role !== 'user' && <AIAgent />}
              <Typography variant={'body-lg-regular'} ariaLabel={msg.content}>
                {renderMessageContent(msg.content, allMergeFields, usersMap)}
              </Typography>
              {msg.widget?.params?.options && (
                <div className={styles.optionsWrap}>
                  {msg.widget.params.options.map((option) => (
                    <Button
                      key={option}
                      text={option}
                      contained={'secondary'}
                      size={'small'}
                      className={styles.optionBtn}
                      disabled={isLoading}
                      onClick={() => {
                        if (getLastUserMessageIndex('assistant') !== index) return
                        sendMessage(option)
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
            {msg.hasError && index === getLastUserMessageIndex('user') && (
              <div className={styles.errorRow}>
                <HelperText
                  paddingBlock={'3.5px'}
                  icon={'alertTriangleSmall'}
                  variant={EnumHelperText.Alert}
                  text={'Something went wrong. Try again.'}
                />
                <Button
                  icon={'refresh'}
                  text={'Retry'}
                  typeBtn={'outlined'}
                  contained={'secondary'}
                  size={'small'}
                  disabled={isLoading}
                  onClick={retry}
                />
              </div>
            )}
          </div>
        ))}
      </>
    )
  }
)
